import React, { CSSProperties, useEffect, useMemo, useState } from 'react';

import {
  AuditOutlined,
  CheckOutlined,
  DislikeOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  FlagOutlined,
  LikeOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  RedoOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import {
  DailyWorkflowTaskResolution,
  ErpOrderStatus,
  ExcludeDurationOption,
  TenantFeatureFlag,
} from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import {
  DeleteDailyWorkflowTaskResolutionQueryParams,
  DeleteDailyWorkflowTaskResolutionResponse,
} from '@recurrency/core-api-schema/dist/dailyWorkflow/deleteDailyWorkflowTaskResolution';
import { GetDailyWorkflowItemLocationDataResponse } from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowItemLocationData';
import { DailyWorkflowRuleInfoDTOList } from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowTasks';
import { DailyWorkflowResolutionData } from '@recurrency/core-api-schema/dist/dailyWorkflow/shared';
import {
  UpdateDailyWorkflowTaskBodyParams,
  UpdateDailyWorkflowTaskResponse,
} from '@recurrency/core-api-schema/dist/dailyWorkflow/updateDailyWorkflowTask';
import { SearchForecastDTO } from '@recurrency/core-api-schema/dist/search/getSearchForecasts';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import { PurchaseOrderLineDTO } from '@recurrency/core-api-schema/dist/v2/purchaseOrders';
import { RuleFailuresDTO } from '@recurrency/core-api-schema/dist/validationRules/getCurrentValidationRuleFailures';
import { AxiosResponse } from 'axios';
import { theme } from 'theme';

import { FlagItemLocationModal } from 'pages/purchasing/PlanningPage/modals/FlagItemLocationModal';
import { MultiLeadTimeModal } from 'pages/purchasing/PlanningPage/modals/MultiLeadTimeModal';
import { MultiMinOrderQuantityModal } from 'pages/purchasing/PlanningPage/modals/MultiMinOrderQuantityModal';
import { MultiSafetyStockModal } from 'pages/purchasing/PlanningPage/modals/MultiSafetyStockModal';
import { MultiStockFloorModal } from 'pages/purchasing/PlanningPage/modals/MultiStockFloorModal';
import { PlanningSupplierLocationSettingsModal } from 'pages/purchasing/PlanningPage/modals/PlanningSupplierLocationSettingsModal';
import { ProjectedInventoryChangePopover } from 'pages/purchasing/PlanningPage/modals/ProjectedInventoryChangePopover';
import {
  convertDailyWorkflowDataToPlanningItem,
  getIsoDateForExcludeDurationOption,
} from 'pages/purchasing/PlanningPage/modals/utils';
import { ReplenishmentPathsPanelContent } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/ReplenishmentPathsPanelContent';
import { useAggregatedNetstockEnabled } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/utils';
import { FORECAST_COLOR, ReplenishmentMethod, USAGE_COLOR } from 'pages/purchasing/utils';

import { Button } from 'components/Button';
import { FlexSpace } from 'components/FlexSpace';
import { FlexSpacer } from 'components/FlexSpacer';
import { NetStockPopover } from 'components/recipes/equation/NetStockPopover';
import { getUsageTableRowsFromForecastRecord } from 'components/recipes/InventoryUsageForecastView';
import { PurchaseOrderLinesTable } from 'components/recipes/PurchaseOrderLinesTable';
import { SalesInvoiceLinesTable } from 'components/recipes/SalesInvoiceLinesTable';
import { UsageForecastModal } from 'components/recipes/sidePane/InventoryUsagePanelContent';
import { QtyByMonthSparkline } from 'components/recipes/sidePane/QtyByMonthSparkline';
import { getMergedForecastRecords } from 'components/recipes/sidePane/sidePane.utils';
import { UnitOfMeasuresPanelContent } from 'components/recipes/sidePane/UnitOfMeasuresPanelContent';
import { ViewAllModal } from 'components/recipes/sidePane/ViewAllModal';
import { Sparkline } from 'components/Sparkline/Sparkline';
import { getMonthlyFilledSeries } from 'components/Sparkline/utils';
import { DEFAULT_PAGE_SIZE } from 'components/Table';
import { Tooltip } from 'components/Tooltip';
import { DemandPattern, demandPatternTooltipMapping } from 'components/Tooltip/DemandPatternTooltip';
import { Typography } from 'components/Typography';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';
import { usePromise } from 'hooks/usePromise';

import { showAsyncModal } from 'utils/asyncModal';
import { captureError } from 'utils/error';
import {
  formatDate,
  formatDateShorthand,
  formatNumber,
  formatPercent,
  formatUSD,
  splitIdNameStr,
  splitIfIdNameStr,
} from 'utils/formatting';
import { shouldShowFeatureFlag } from 'utils/roleAndTenant';
import { searchIndex } from 'utils/search/search';
import { getTenantSetting } from 'utils/tenantSettings';
import { DailyWorkflowTrackEventSource, track, TrackEvent } from 'utils/track';

import { RecommendedMaxPopover } from '../purchasing/PlanningPage/modals/RecommendedMaxPopover';
import { RecommendedMinPopover } from '../purchasing/PlanningPage/modals/RecommendedMinPopover';
import { AuditLogModal } from './AudiLogModal';
import { DailyWorkflowCardBase } from './DailyWorkflowCardBase';
import { DailyWorkflowEmptyState } from './DailyWorkflowEmptyState';
import { DailyWorkflowGraphCard } from './DailyWorkflowGraphCard';
import { DailyWorkflowLoadingSkeleton } from './DailyWorkflowLoadingSkeleton';
import { DailyWorkflowValueCard } from './DailyWorkflowValueCard';
import { DemandPatternBadge } from './DemandPatternBadge';
import { KeyboardShortcutButton } from './KeyboardShortcutButton';
import { NoChartData } from './NoDataChart';
import { DailyWorkflowPageState, ItemWithResolutionData } from './types';
import { calculateProjectedFillRateV2, getProjectedInventoryChange, parseGutCheckData } from './utils';

const OLD_DATE = new Date('01-01-1970');
const MONTH_IN_MS = 30 * 24 * 60 * 60 * 1000;

interface DailyWorkflowContentProps {
  dailyWorkflowResponse?: GetDailyWorkflowItemLocationDataResponse;
  isDailyWorkflowLoading: boolean;
  reloadPlanning: () => void;
  dailyWorkflowTasksData?: DailyWorkflowRuleInfoDTOList;
  onAccept: (
    resolutionData: ItemWithResolutionData,
  ) => Promise<AxiosResponse<UpdateDailyWorkflowTaskResponse>> | unknown;
  onReset: (
    resolutionData: DeleteDailyWorkflowTaskResolutionQueryParams,
  ) => Promise<AxiosResponse<DeleteDailyWorkflowTaskResolutionResponse>> | unknown;
  onOverrideAndExclude: (
    resolutionData: ItemWithResolutionData,
  ) => Promise<AxiosResponse<UpdateDailyWorkflowTaskResponse>> | { data: { success: boolean } };
  onFlag: (
    resolutionData: ItemWithResolutionData,
  ) => Promise<AxiosResponse<UpdateDailyWorkflowTaskResponse>> | { data: { success: boolean } };
  pageState: DailyWorkflowPageState;
  setPageState: React.Dispatch<React.SetStateAction<DailyWorkflowPageState>>;
  ruleIdx: number;
  trackEventSource: DailyWorkflowTrackEventSource;
}

export function DailyWorkflowContent({
  dailyWorkflowResponse,
  isDailyWorkflowLoading,
  reloadPlanning,
  dailyWorkflowTasksData,
  onAccept,
  onReset,
  onOverrideAndExclude,
  onFlag,
  pageState,
  setPageState,
  ruleIdx,
  trackEventSource,
}: DailyWorkflowContentProps) {
  const dailyWorkflowData = useMemo(() => {
    if (!dailyWorkflowResponse || !dailyWorkflowTasksData) return undefined;
    return dailyWorkflowResponse.items[pageState.selectedIndex];
  }, [dailyWorkflowResponse, dailyWorkflowTasksData, pageState.selectedIndex]);

  const { activeTenant, activeUser } = useGlobalApp();
  const useGutCheckV2Endpoint = shouldShowFeatureFlag(
    activeTenant,
    activeUser,
    TenantFeatureFlag.DailyWorkflowUseGutCheckV2,
  );

  // This is a hack to specifically hide it for bart. revisit this once we get better use-specific customization
  const hideGutCheck = activeUser.email === 'bartcayce@caycemill.com';

  const itemLocationString = useMemo(
    () =>
      dailyWorkflowData ? `${dailyWorkflowData.itemId}|${splitIdNameStr(dailyWorkflowData.location).foreignId}` : '',
    [dailyWorkflowData],
  );

  const isOpOq = useMemo(
    () => dailyWorkflowData?.erpReplenishmentMethod === ReplenishmentMethod.OPOQ,
    [dailyWorkflowData],
  );

  const updatePageState = (resolutionData?: DailyWorkflowResolutionData) => {
    setPageState((prevState) => ({
      ...prevState,
      resolutions: {
        ...prevState.resolutions,
        [itemLocationString]: {
          ruleId: pageState.selectedRuleId,
          resolutionData,
        },
      },
    }));
  };

  const prevResolutionData = useMemo(() => {
    if (pageState.resolutions && pageState.resolutions[itemLocationString]) {
      return pageState.resolutions[itemLocationString].resolutionData;
    }
    if (dailyWorkflowData) {
      return dailyWorkflowData.resolutionData;
    }
    return undefined;
  }, [pageState, itemLocationString, dailyWorkflowData]);

  const min = useMemo(() => {
    if (!dailyWorkflowData) return 0;
    if (prevResolutionData) {
      return prevResolutionData.min;
    }
    return dailyWorkflowData.recommendedMin;
  }, [prevResolutionData, dailyWorkflowData]);

  const max = useMemo(() => {
    if (!dailyWorkflowData) return 0;
    if (prevResolutionData) {
      return prevResolutionData.max;
    }
    return dailyWorkflowData.recommendedMax;
  }, [prevResolutionData, dailyWorkflowData]);

  const forecastRecordIds = dailyWorkflowData
    ? [`${dailyWorkflowData.itemId}|${splitIdNameStr(dailyWorkflowData.location).foreignId}`]
    : [];

  const planningLocationId = useMemo(
    () => (dailyWorkflowData?.location ? splitIdNameStr(dailyWorkflowData.location).foreignId : ''),
    [dailyWorkflowData],
  );

  const searchPlanningItem = useMemo(() => {
    if (!dailyWorkflowData) {
      return undefined;
    }
    return convertDailyWorkflowDataToPlanningItem(dailyWorkflowData);
  }, [dailyWorkflowData]);

  const { enabled: useAggregatedNetstock } = useAggregatedNetstockEnabled();

  /*
   * Search Forecast data and calculations
   */
  const { data: forecastResponse, isLoading: isForecastLoading } = usePromise(
    () =>
      searchIndex<SearchForecastDTO>({
        indexName: SearchIndexName.Forecasts,
        filters: { id: forecastRecordIds },
      }),
    [dailyWorkflowData],
    {
      cacheKey: `searchframe/${SearchIndexName.Forecasts}:${forecastRecordIds.join(',')}`,
      onError: captureError,
    },
  );
  const forecastRecords = forecastResponse?.hits;
  const forecastRecord = getMergedForecastRecords(forecastRecords);
  const usageTableRows = getUsageTableRowsFromForecastRecord(forecastRecord);

  const forecastedAverage = useMemo(
    () =>
      forecastRecord
        ? forecastRecord.forecast_demand.reduce((acc, curr) => acc + curr, 0) / forecastRecord.forecast_demand.length
        : 0,
    [forecastRecord],
  );

  // This is used to calculate past monthly and yearly averages.
  // It should only look at the last 12 months of data.
  // usageTableRows are sorted newest to oldest.
  const pastAverage = useMemo(() => {
    if (!usageTableRows) return 0;
    const last12Months = usageTableRows.slice(0, 12);
    return last12Months.reduce((acc, curr) => acc + curr.usage, 0) / last12Months.length;
  }, [usageTableRows]);

  /*
   * Sales History data and calculations
   */
  const { data: salesHistoryData } = useCoreApi(schemas.salesInvoiceLines.getSalesInvoiceLinesV2, {
    queryParams: {
      offset: 0,
      limit: DEFAULT_PAGE_SIZE,
      filter: dailyWorkflowData
        ? { itemId: dailyWorkflowData.itemCode, locationIds: [splitIdNameStr(dailyWorkflowData.location).foreignId] }
        : undefined,
    },
  });

  const date12MonthsAgo = useMemo(() => new Date(Date.now() - 12 * MONTH_IN_MS), []);
  const ordersLast12Months = useMemo(
    () => salesHistoryData?.items.filter((item) => new Date(item.invoiceDate) >= date12MonthsAgo),
    [date12MonthsAgo, salesHistoryData?.items],
  );
  const aggsLast12Months = useMemo(
    () => salesHistoryData?.aggs.filter((agg) => new Date(agg.invoiceDate) >= date12MonthsAgo),
    [date12MonthsAgo, salesHistoryData?.aggs],
  );
  const averageOrderSize = useMemo(
    () =>
      ordersLast12Months
        ? ordersLast12Months.reduce((acc, curr) => acc + (curr.quantityShipped ?? 0), 0) / ordersLast12Months.length
        : 0,
    [ordersLast12Months],
  );

  /*
   * Purchase Order Lines data and calculations
   */
  const { data: purchaseOrderLinesData } = useCoreApi(schemas.purchaseOrderLines.getPurchaseOrderLinesV2, {
    queryParams: {
      offset: 0,
      limit: DEFAULT_PAGE_SIZE,
      filter: dailyWorkflowData
        ? { itemId: dailyWorkflowData?.itemCode, locationIds: [splitIdNameStr(dailyWorkflowData?.location).foreignId] }
        : {},
    },
  });

  const purchaseOrderLinesLast12Months = useMemo(
    () =>
      purchaseOrderLinesData?.items.filter(
        (item) => new Date(item.orderDate || '') >= date12MonthsAgo && item.status !== 'canceled',
      ),
    [date12MonthsAgo, purchaseOrderLinesData?.items],
  );
  const aggsPurchaseOrderLinesLast12Months = useMemo(
    () => purchaseOrderLinesData?.aggs.filter((agg) => new Date(agg.orderDate) >= date12MonthsAgo),
    [date12MonthsAgo, purchaseOrderLinesData?.aggs],
  );
  const averagePurchaseOrderSize = useMemo(
    () =>
      purchaseOrderLinesLast12Months
        ? purchaseOrderLinesLast12Months.reduce((acc, curr) => acc + (curr.quantityOrdered ?? 0), 0) /
          purchaseOrderLinesLast12Months.length
        : 0,
    [purchaseOrderLinesLast12Months],
  );
  const openPurchaseOrderCount = useMemo(
    () => purchaseOrderLinesLast12Months?.filter((item) => item.status === ErpOrderStatus.Open).length || 0,
    [purchaseOrderLinesLast12Months],
  );
  const lastPurchaseOrder = useMemo(
    () =>
      purchaseOrderLinesData?.items.reduce(
        (acc, curr) => {
          // If the order date is not set, use the oldest date as a starting point
          const currDate = curr.orderDate ? new Date(curr.orderDate) : OLD_DATE;
          const accDate = acc.orderDate ? new Date(acc.orderDate) : OLD_DATE;
          return currDate > accDate ? curr : acc;
        },
        { orderDate: undefined } as PurchaseOrderLineDTO,
      ),
    [purchaseOrderLinesData?.items],
  );
  const lastPurchaseOrderDate = lastPurchaseOrder?.orderDate || '';
  const lastPurchaseOrderAmount = lastPurchaseOrder?.quantityOrdered || 0;

  /*
   * Validation Rules data and calculations
   */
  const validationRuleFailuresApiRes = useCoreApi(schemas.validationRules.getCurrentValidationRuleFailures, {
    queryParams: {
      itemId: dailyWorkflowData?.itemId ?? '',
      locationId: planningLocationId,
    },
  });

  const validationRuleFailures = useMemo(
    () => validationRuleFailuresApiRes?.data?.items || [],
    [validationRuleFailuresApiRes?.data?.items],
  );

  const latestValidationRuleRunTime = useMemo(
    () =>
      validationRuleFailures.reduce(
        (acc, curr) => {
          if (new Date(curr.runTime) > new Date(acc.runTime)) {
            return curr;
          }
          return acc;
        },
        { runTime: date12MonthsAgo.toISOString() } as RuleFailuresDTO,
      ),
    [date12MonthsAgo, validationRuleFailures],
  );

  /*
   * Critical Information data and calculations
   */
  const criticalInformationRows = useMemo(
    () =>
      dailyWorkflowData
        ? [
            { title: 'ABC Class', value: dailyWorkflowData.purchaseClass || '' },
            {
              title: 'Lead Time',
              value: `${dailyWorkflowData.leadTime || 0} days`,
              onClick: () => {
                if (!searchPlanningItem) return;
                showAsyncModal(MultiLeadTimeModal, {
                  selection: { type: 'list', items: [searchPlanningItem], count: 1 },
                  searchIndexReload: reloadPlanning,
                });
              },
            },
            {
              title: 'Safety Stock',
              value: `${dailyWorkflowData.safetyStock || 0} days`,
              onClick: () => {
                if (!searchPlanningItem) return;
                showAsyncModal(MultiSafetyStockModal, {
                  selection: { type: 'list', items: [searchPlanningItem], count: 1 },
                  searchIndexReload: reloadPlanning,
                });
              },
            },
            {
              title: 'Order Cycle Time',
              value: `${dailyWorkflowData.orderCycle || 0} days`,
              onClick: () => {
                showAsyncModal(PlanningSupplierLocationSettingsModal, {
                  supplierId: splitIfIdNameStr(dailyWorkflowData.primarySupplier)?.foreignId ?? '',
                  locationId: splitIdNameStr(dailyWorkflowData.location).foreignId,
                  searchIndexReload: reloadPlanning,
                });
              },
            },
            {
              title: 'Stock Floor',
              value: dailyWorkflowData.stockFloor ? `${dailyWorkflowData.stockFloor}` : 'None',
              onClick: () => {
                if (!searchPlanningItem) return;
                showAsyncModal(MultiStockFloorModal, {
                  selection: { type: 'list', items: [searchPlanningItem], count: 1 },
                  searchIndexReload: reloadPlanning,
                });
              },
            },
            {
              title: 'Min Order Quantity',
              value: dailyWorkflowData.minOrderQuantity ? `${dailyWorkflowData.minOrderQuantity}` : 'None',
              onClick: () => {
                if (!searchPlanningItem) return;
                showAsyncModal(MultiMinOrderQuantityModal, {
                  selection: { type: 'list', items: [searchPlanningItem], count: 1 },
                  searchIndexReload: reloadPlanning,
                });
              },
            },
            {
              title: 'Primary Supplier',
              value: dailyWorkflowData.primarySupplier ? `${dailyWorkflowData.primarySupplier}` : 'None',
            },
            {
              title: 'Supplier Part ID',
              value: dailyWorkflowData.supplierPartNumber ? `${dailyWorkflowData.supplierPartNumber}` : 'None',
            },
            {
              title: 'ERP Replenishment Method',
              value: dailyWorkflowData.erpReplenishmentMethod ? `${dailyWorkflowData.erpReplenishmentMethod}` : 'None',
            },
          ]
        : [],
    [dailyWorkflowData, reloadPlanning, searchPlanningItem],
  );

  // This is to track which tab target is currently focused
  const [target, setTarget] = useState<number>(2);
  const excludeDuration =
    getTenantSetting(TenantSettingKey.FormExcludeDefaultDuration) ?? ExcludeDurationOption.OneMonth;
  const excludedUntil = useMemo(() => getIsoDateForExcludeDurationOption(excludeDuration), [excludeDuration]);

  // This is to handle switching between the two tab targets of Min and Max
  const handleTargetChange = (currentTarget: number) => {
    let newTarget = 0;
    if (currentTarget === 1) {
      newTarget = 2;
    } else {
      newTarget = 1;
    }
    document.getElementById(`target-${newTarget}`)?.focus();
    setTarget(newTarget);
  };

  /**
   * Projected Inventory Change calculation
   */
  const calculatedProjectedInventoryChange = useMemo(() => {
    if (!dailyWorkflowData) return 0;
    return getProjectedInventoryChange(dailyWorkflowData, min, max);
  }, [dailyWorkflowData, min, max]);

  /*
   * Projected Fill Rate calculations
   */
  const currentFillRate = useMemo(() => {
    if (!dailyWorkflowData) return 0;
    return calculateProjectedFillRateV2(
      dailyWorkflowData.currentMin,
      dailyWorkflowData.currentMax,
      dailyWorkflowData.historicalDemand,
      dailyWorkflowData.leadTime,
    );
  }, [dailyWorkflowData]);

  const calculatedProjectedFillRate = useMemo(() => {
    if (!dailyWorkflowData) return 0;
    const fillRate = calculateProjectedFillRateV2(
      min,
      max,
      dailyWorkflowData.historicalDemand,
      dailyWorkflowData.leadTime,
    );
    return fillRate;
  }, [dailyWorkflowData, min, max]);

  /**
   * Moves to the next or previous item in the current rule, or jumps to the next/previous rule if at the end/beginning.
   */
  const handleMoveItem = (direction: 'next' | 'prev') => {
    if (!dailyWorkflowResponse || !dailyWorkflowTasksData) return;

    let newIndex = pageState.selectedIndex;
    if (direction === 'next') {
      newIndex = pageState.selectedIndex + 1;
      if (newIndex < (dailyWorkflowResponse.items.length || 0)) {
        setPageState((prev) => ({
          ...prev,
          selectedIndex: newIndex,
        }));
      } else if (newIndex === dailyWorkflowResponse.items.length) {
        // At the last item, move to the next rule
        if (ruleIdx >= 0 && ruleIdx + 1 < dailyWorkflowTasksData.items.filter((i) => i.taskCount > 0).length) {
          setPageState((prev) => ({
            ...prev,
            selectedRuleId: dailyWorkflowTasksData.items[ruleIdx + 1].ruleId,
            selectedIndex: 0,
          }));
        }
      }
    } else if (direction === 'prev') {
      newIndex = pageState.selectedIndex - 1;
      if (newIndex >= 0) {
        setPageState((prev) => ({
          ...prev,
          selectedIndex: newIndex,
        }));
      } else if (ruleIdx > 0) {
        // At the first item, move to the previous rule
        setPageState((prev) => ({
          ...prev,
          selectedRuleId: dailyWorkflowTasksData.items[ruleIdx - 1].ruleId,
        }));
        const prevRule = dailyWorkflowTasksData.items[ruleIdx - 1];
        const prevRuleUnsubmittedCount = prevRule.taskCount - prevRule.submittedItemLocations.length;
        if (prevRuleUnsubmittedCount > 0) {
          setPageState((prev) => ({
            ...prev,
            selectedIndex: prevRuleUnsubmittedCount - 1,
          }));
        } else {
          setPageState((prev) => ({
            ...prev,
            selectedIndex: 0,
          }));
        }
      }
    }
  };

  const handleAccept = async () => {
    if (!dailyWorkflowData || !validateMinAndMax()) return;

    const resolutionData: UpdateDailyWorkflowTaskBodyParams['resolutionData'] = {
      min,
      max,
      currentMin: dailyWorkflowData.currentMin,
      currentMax: dailyWorkflowData.currentMax,
      recommendedMin: dailyWorkflowData.recommendedMin,
      recommendedMax: dailyWorkflowData.recommendedMax,
      projectedInventoryChange: calculatedProjectedInventoryChange,
      replenishmentMethod: dailyWorkflowData.erpReplenishmentMethod,
    };

    const bodyParams: ItemWithResolutionData = {
      itemId: dailyWorkflowData.itemId,
      locationId: splitIdNameStr(dailyWorkflowData.location).foreignId,
      itemCode: dailyWorkflowData.itemCode,
      locationName: dailyWorkflowData.location,
      resolution:
        min === dailyWorkflowData.recommendedMin && max === dailyWorkflowData.recommendedMax
          ? DailyWorkflowTaskResolution.Accepted
          : DailyWorkflowTaskResolution.Overriden,
      resolutionData,
    };

    await onAccept(bodyParams);

    updatePageState(resolutionData);

    handleMoveItem('next');

    track(TrackEvent.DailyWorkflow_AcceptDailyWorkflowItem, {
      itemId: dailyWorkflowData.itemId,
      locationId: splitIdNameStr(dailyWorkflowData.location).foreignId,
      source: trackEventSource,
      ...resolutionData,
    });
  };

  const handleReset = async () => {
    if (!dailyWorkflowData) return;

    const queryParams: DeleteDailyWorkflowTaskResolutionQueryParams = {
      itemId: dailyWorkflowData.itemId,
      locationId: splitIdNameStr(dailyWorkflowData.location).foreignId,
    };

    await onReset(queryParams);

    setPageState((prevState) => {
      const newResolutions = { ...prevState.resolutions };
      delete newResolutions[itemLocationString];

      return {
        ...prevState,
        resolutions: newResolutions,
      };
    });
  };

  const previouslyAccepted = useMemo(
    () =>
      prevResolutionData &&
      !prevResolutionData.flagReason &&
      !prevResolutionData.excludeUntil &&
      prevResolutionData.replenishmentMethod,
    [prevResolutionData],
  );

  const previouslyOverridden = useMemo(
    () =>
      // check for either overrides or exclusions
      prevResolutionData && prevResolutionData.excludeUntil,
    [prevResolutionData],
  );

  const gutCheckApplicationId = getTenantSetting(TenantSettingKey.FeatureGutCheckApplicationId);

  const { data: gutCheckRawData, isLoading: isGutCheckLoading } = useCoreApi(
    useGutCheckV2Endpoint ? schemas.validationRules.postGutCheckV2 : schemas.validationRules.postGutCheck,
    pageState.gutChecks[itemLocationString] || !dailyWorkflowData || hideGutCheck
      ? null
      : {
          bodyParams: {
            applicationId: gutCheckApplicationId,
            input: JSON.stringify({
              ...dailyWorkflowData,
              projectedInventoryChange: calculatedProjectedInventoryChange,
              projectedFillRate: calculatedProjectedFillRate,
            }),
          },
        },
  );

  useEffect(() => {
    if (gutCheckRawData?.response) {
      const parsedData = parseGutCheckData(gutCheckRawData.response);
      if (parsedData.explanation !== '' && parsedData.recommendedAction !== '') {
        setPageState((prev) => ({
          ...prev,
          gutChecks: { ...prev.gutChecks, [itemLocationString]: parsedData },
        }));
      }
    }
  }, [gutCheckRawData, itemLocationString, setPageState]);

  function validateMinAndMax() {
    return isOpOq ? min >= 0 && max > 0 : min <= max;
  }

  // Action button styling helper functions.
  const getActionButtonStyle = (actionType: string) => {
    const isRecommended = pageState.gutChecks[itemLocationString]?.recommendedAction === actionType;
    // Important is used because of antd overriding colors with variant
    return css`
      ${ActionButtonStyle}
      ${isRecommended
        ? `
        border: 2px solid ${theme.colors.primary[500]} !important;    
        &:not(:disabled) {
          border-color: ${theme.colors.primary[500]} !important;
        }
      `
        : ''}
    `;
  };

  const getButtonTextStyle = (actionType: string, isSelected: boolean) => {
    if (isSelected) return SelectedActionButtonStyle;
    const isRecommended = pageState.gutChecks[itemLocationString]?.recommendedAction === actionType;
    if (isRecommended) return { ...DefaultButtonStyle, color: theme.colors.primary[500] };
    return DefaultButtonStyle;
  };

  const getIconColor = (actionType: string, isSelected: boolean) => {
    const isRecommended = pageState.gutChecks[itemLocationString]?.recommendedAction === actionType;
    return isRecommended && !isSelected ? theme.colors.primary[500] : 'inherit';
  };

  if (isDailyWorkflowLoading) return <DailyWorkflowLoadingSkeleton />;
  if (!dailyWorkflowData) return <DailyWorkflowEmptyState />;
  return (
    <div
      className={css`
        flex: 1 0 400px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        border: 1px solid ${theme.colors.neutral[300]};
        border-radius: 8px;
        margin-bottom: auto;
      `}
    >
      <div
        className={css`
          padding: 12px 16px;
          gap: 16px;
          border-bottom: 1px solid ${theme.colors.neutral[300]};
        `}
      >
        <FlexSpace direction="row" gap={8}>
          <Typography style={{ fontSize: 16, fontWeight: 'bolder' }}>
            Item #
            <Typography style={{ display: 'inline-block', fontWeight: 'bolder' }} copyable>
              {dailyWorkflowData.itemCode}
            </Typography>
          </Typography>
          <Typography style={{ fontSize: 16 }}>at {dailyWorkflowData.location}</Typography>
          {dailyWorkflowData.demandPattern && (
            <Tooltip
              title={<span>{demandPatternTooltipMapping[dailyWorkflowData.demandPattern as DemandPattern]}</span>}
              overlayInnerStyle={{ margin: 'auto' }}
            >
              <span>
                <DemandPatternBadge demandPattern={dailyWorkflowData.demandPattern} />
              </span>
            </Tooltip>
          )}
          <FlexSpacer />
          <FlexSpace direction="row" gap={8}>
            <KeyboardShortcutButton
              variant="secondary"
              label="Prev"
              shortcut="J"
              disabled={
                pageState.selectedIndex === 0 &&
                (ruleIdx === 0 ||
                  // If the previous rule has no unsubmitted items, disable the button
                  (dailyWorkflowTasksData?.items[ruleIdx - 1]?.submittedItemLocations.length || 0) ===
                    (dailyWorkflowTasksData?.items[ruleIdx - 1]?.taskCount || 0))
              }
              onClick={() => {
                handleMoveItem('prev');
              }}
            />
            <KeyboardShortcutButton
              variant="secondary"
              label="Skip"
              shortcut="K"
              disabled={
                // Disable the button if we're at the last item of the last non-empty rule
                pageState.selectedIndex === (dailyWorkflowResponse?.items.length || 0) - 1 &&
                ruleIdx === (dailyWorkflowTasksData?.items.filter((rule) => rule.taskCount > 0).length || 0) - 1
              }
              onClick={() => {
                handleMoveItem('next');
              }}
            />
            <KeyboardShortcutButton
              disabled={!dailyWorkflowData || !validateMinAndMax()}
              variant="primary"
              label="Accept"
              shortcut="L"
              onClick={handleAccept}
            />
          </FlexSpace>
        </FlexSpace>
        <Typography style={{ fontSize: 18, fontWeight: 700, marginBottom: '0.5em', lineHeight: 1.4 }}>
          {dailyWorkflowData.itemName}
        </Typography>
        {dailyWorkflowData.itemDescription && (
          <Typography type="subtitle">{dailyWorkflowData.itemDescription}</Typography>
        )}
      </div>
      <FlexSpace direction="row" gap={0}>
        <div
          className={css`
            padding: 20px;
            padding-top: 12px;
            gap: 16px;
            border-right: 1px solid ${theme.colors.neutral[300]};
            width: 100%;
          `}
        >
          <FlexSpace direction="row" gap={8} wrap>
            <DailyWorkflowValueCard
              title={isOpOq ? 'Order Point' : 'Minimum'}
              headerActions={
                searchPlanningItem && <RecommendedMinPopover record={searchPlanningItem} showOpOqVariant={isOpOq} />
              }
              currentValue={dailyWorkflowData.currentMin}
              targetValue={min}
              subText={`${dailyWorkflowData.planningUnitOfMeasure} units`}
              isEditable
              validate={validateMinAndMax}
              keyboardShortcut="tab"
              showChange
              onKeyboardShortcutClick={() => {
                handleTargetChange(target);
              }}
              onChange={(value: number) => {
                updatePageState({
                  min: value,
                  max,
                  currentMin: dailyWorkflowData.currentMin,
                  currentMax: dailyWorkflowData.currentMax,
                  recommendedMin: dailyWorkflowData.recommendedMin,
                  recommendedMax: dailyWorkflowData.recommendedMax,
                  projectedInventoryChange: calculatedProjectedInventoryChange,
                });
              }}
              inputId="target-1"
              flexGrow={1}
            />
            <DailyWorkflowValueCard
              title={isOpOq ? 'Order Quantity' : 'Maximum'}
              headerActions={searchPlanningItem && !isOpOq && <RecommendedMaxPopover record={searchPlanningItem} />}
              currentValue={dailyWorkflowData.currentMax}
              targetValue={max}
              subText={`${dailyWorkflowData.planningUnitOfMeasure} units`}
              isEditable
              validate={validateMinAndMax}
              keyboardShortcut="tab"
              showChange
              onKeyboardShortcutClick={() => {
                handleTargetChange(target);
              }}
              onChange={(value: number) => {
                updatePageState({
                  min,
                  max: value,
                  currentMin: dailyWorkflowData.currentMin,
                  currentMax: dailyWorkflowData.currentMax,
                  recommendedMin: dailyWorkflowData.recommendedMin,
                  recommendedMax: dailyWorkflowData.recommendedMax,
                  projectedInventoryChange: calculatedProjectedInventoryChange,
                });
              }}
              inputId="target-2"
              flexGrow={1}
            />
            <DailyWorkflowValueCard
              title="Unit Cost"
              currentValue={Number(dailyWorkflowData.unitCost)}
              isEditable={false}
              onChange={() => {}}
              subText="USD value"
              formatFn={(value) => formatUSD(value, true, 2)}
              flexGrow={1}
            />
            <DailyWorkflowValueCard
              title="Projected Inventory Change"
              headerActions={
                searchPlanningItem && (
                  <ProjectedInventoryChangePopover
                    record={{
                      // We pass the user inputted values to the popover
                      ...searchPlanningItem,
                      recommended_min: min,
                      recommended_max: max,
                      projected_inventory_change: calculatedProjectedInventoryChange,
                    }}
                    context="dailyWorkflow"
                  />
                )
              }
              currentValue={calculatedProjectedInventoryChange || 0}
              isEditable={false}
              onChange={() => {}}
              subText="USD value change"
              formatFn={(value) => (
                <Typography
                  style={{
                    color: value === 0 ? 'inherit' : value < 0 ? theme.colors.success[500] : theme.colors.danger[700],
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                >
                  {value > 0 ? '+' : ''}
                  {formatUSD(value, true, 2)}
                </Typography>
              )}
              flexGrow={2}
            />
            <DailyWorkflowValueCard
              title="Projected Service Level"
              headerActions={
                <Tooltip title="Probability you’ll avoid a stockout after replenishing to this demand plan. Higher % = better service level.">
                  <QuestionCircleOutlined />
                </Tooltip>
              }
              currentValue={currentFillRate}
              targetValue={calculatedProjectedFillRate}
              isEditable={false}
              showChange
              onChange={() => {}}
              formatFn={(value) => (value ? formatPercent(value, 0) : '0%')}
              flexGrow={1}
              useFlatDifference
            />
          </FlexSpace>
          <FlexSpace
            className={css`
              margin-top: 8px;
            `}
            direction="row"
            gap={8}
          >
            <FlexSpace
              className={css`
                margin-bottom: auto;
                max-width: 50%;
                display: flex;
                flex-grow: 2;
              `}
              direction="column"
              gap={8}
            >
              {forecastRecord && usageTableRows ? (
                <DailyWorkflowGraphCard
                  title="Usage and Forecast"
                  sparkline={
                    <Sparkline
                      fullWidth
                      className={css`
                        margin-top: 10px;
                      `}
                      height={75}
                      series={[
                        ...getMonthlyFilledSeries({
                          data: usageTableRows,
                          dateField: 'date',
                          valueField: 'usage',
                          numPrevYears: 1,
                          // Include current month to show both usage and forecast dots
                          curMonthOffset: 0,
                          title: 'Qty Used',
                          formatFn: (value: number) => `${value} ${forecastRecord?.unit_of_measure ?? ''}`,
                          color: USAGE_COLOR,
                          showFullYear: false,
                        }),
                        {
                          data: forecastRecord.forecast_dates.slice(0, 6 /* 6 month forecast */).map((_, i) => ({
                            date: forecastRecord.forecast_dates[i],
                            value: forecastRecord.forecast_demand[i] ?? 0,
                            dotted: true,
                          })),
                          title: 'Qty Forecasted',
                          formatFn: (value: number) => `${value} ${forecastRecord?.unit_of_measure ?? ''}`,
                          color: FORECAST_COLOR,
                        },
                      ]}
                      showAxisLabels
                    />
                  }
                  dataPoints={[
                    {
                      title: 'Forecasted Monthly Average',
                      value: `${formatNumber(forecastedAverage, 2)} ${forecastRecord?.unit_of_measure ?? ''}`,
                    },
                    {
                      title: 'Yearly Forecast',
                      value: `${formatNumber(forecastedAverage * 12, 2)} ${forecastRecord?.unit_of_measure ?? ''}`,
                    },
                    {
                      title: 'Past Monthly Average',
                      value: `${formatNumber(pastAverage, 2)} ${forecastRecord?.unit_of_measure ?? ''}`,
                    },
                    {
                      title: 'Past Yearly Average',
                      value: `${formatNumber(pastAverage * 12, 2)} ${forecastRecord?.unit_of_measure ?? ''}`,
                    },
                  ]}
                  onFullScreenClick={() => {
                    showAsyncModal(UsageForecastModal, {
                      itemId: dailyWorkflowData.itemCode,
                      itemUid: dailyWorkflowData.itemId,
                      locations: [splitIdNameStr(dailyWorkflowData.location)],
                      forecastRecords,
                      isLoading: isDailyWorkflowLoading || isForecastLoading,
                    });
                  }}
                />
              ) : (
                <></>
              )}
              {aggsLast12Months ? (
                <DailyWorkflowGraphCard
                  title="Sales History (Last 12 Months)"
                  sparkline={
                    aggsLast12Months && aggsLast12Months.length > 0 ? (
                      <div
                        className={css`
                          margin-top: 10px;
                          width: 100%;
                        `}
                      >
                        <QtyByMonthSparkline
                          data={aggsLast12Months}
                          dateField="invoiceDate"
                          quantityField="quantityShipped"
                          seriesTitle="Qty Shipped"
                          height={75}
                          fullWidth
                          numPrevYears={1}
                          showFullYear={false}
                          showAxisLabels
                        />
                      </div>
                    ) : (
                      <NoChartData title="No sales history" />
                    )
                  }
                  dataPoints={[
                    {
                      title: 'Total Orders',
                      value: `${ordersLast12Months?.length || 0}`,
                    },
                    {
                      title: 'Average Order Frequency',
                      value: `${formatNumber((ordersLast12Months?.length || 0) / 12, 2)} orders / month`,
                    },
                    {
                      title: 'Average Order Size',
                      value: `${formatNumber(averageOrderSize, 2)} ${dailyWorkflowData.planningUnitOfMeasure}`,
                    },
                    {
                      title: 'Unique Customers',
                      value: `${formatNumber(new Set(ordersLast12Months?.map((item) => item.customerId)).size)}`,
                    },
                  ]}
                  onFullScreenClick={() => {
                    showAsyncModal(ViewAllModal, {
                      title: `Sales History of Item #${dailyWorkflowData.itemCode} to ${
                        splitIdNameStr(dailyWorkflowData.location).name
                      }`,
                      content: (
                        <SalesInvoiceLinesTable
                          itemId={dailyWorkflowData.itemCode}
                          locationIds={[splitIdNameStr(dailyWorkflowData.location).foreignId]}
                        />
                      ),
                    });
                  }}
                />
              ) : (
                <></>
              )}
              {aggsPurchaseOrderLinesLast12Months ? (
                <DailyWorkflowGraphCard
                  title="Purchase Orders (Last 12 Months)"
                  sparkline={
                    aggsPurchaseOrderLinesLast12Months && aggsPurchaseOrderLinesLast12Months.length > 0 ? (
                      <div
                        className={css`
                          margin-top: 10px;
                          width: 100%;
                        `}
                      >
                        <QtyByMonthSparkline
                          data={aggsPurchaseOrderLinesLast12Months}
                          dateField="orderDate"
                          quantityField="quantityOrdered"
                          seriesTitle="Qty Ordered"
                          height={75}
                          fullWidth
                          numPrevYears={1}
                          showFullYear={false}
                          showAxisLabels
                        />
                      </div>
                    ) : (
                      <NoChartData title="No purchase orders" />
                    )
                  }
                  dataPoints={[
                    {
                      title: 'Average PO Size',
                      value: `${formatNumber(averagePurchaseOrderSize, 2)} ${dailyWorkflowData.planningUnitOfMeasure}`,
                    },
                    {
                      title: 'Open POs',
                      value: `${formatNumber(openPurchaseOrderCount)}`,
                    },
                    {
                      title: 'Last PO Date',
                      value: `${formatDateShorthand(lastPurchaseOrderDate)}`,
                    },
                    {
                      title: 'Last PO Amount',
                      value: `${formatNumber(lastPurchaseOrderAmount)}`,
                    },
                  ]}
                  onFullScreenClick={() => {
                    showAsyncModal(ViewAllModal, {
                      title: `Purchase Lines for Item #${dailyWorkflowData.itemCode}`,
                      content: (
                        <PurchaseOrderLinesTable
                          itemId={dailyWorkflowData.itemCode}
                          locationIds={[splitIdNameStr(dailyWorkflowData.location).foreignId]}
                        />
                      ),
                    });
                  }}
                />
              ) : (
                <></>
              )}
            </FlexSpace>
            <FlexSpace
              className={css`
                margin-bottom: auto;
                max-width: 50%;
                display: flex;
                flex-grow: 1;
              `}
              direction="column"
              gap={8}
            >
              <DailyWorkflowCardBase
                title="Triggered Rules"
                headerActions={
                  <div
                    className={css`
                      font-size: 12px;
                      font-weight: 500;
                    `}
                  >
                    <i>
                      Latest Run Time:{' '}
                      {latestValidationRuleRunTime ? formatDateShorthand(latestValidationRuleRunTime?.runTime) : 'N/A'}
                    </i>
                  </div>
                }
              >
                {validationRuleFailures.length > 0 ? (
                  <FlexSpace
                    direction="column"
                    gap={8}
                    className={css`
                      padding: 8px;
                    `}
                  >
                    {validationRuleFailures.map((failure) => (
                      <Tooltip title={failure.ruleDescription} key={failure.ruleId}>
                        <div>
                          <FlexSpace key={failure.ruleId} direction="row" gap={8}>
                            <ExclamationCircleFilled style={{ color: theme.colors.warning[500] }} />
                            <Typography style={{ fontSize: 12 }}>{failure.ruleName}</Typography>
                          </FlexSpace>
                        </div>
                      </Tooltip>
                    ))}
                  </FlexSpace>
                ) : (
                  <div>
                    <Typography>No rules</Typography>
                  </div>
                )}
              </DailyWorkflowCardBase>
              <DailyWorkflowCardBase title="Critical Information" headerActions={<></>} noPadding>
                <FlexSpace
                  direction="column"
                  className={css`
                    width: 100%;
                  `}
                  gap={0}
                >
                  {criticalInformationRows.map(
                    (row: { title: string; value: string; onClick?: () => void }, index: number) => (
                      <div
                        className={css`
                          width: 100%;
                        `}
                        onClick={row.onClick}
                        key={row.title}
                      >
                        <FlexSpace
                          direction="row"
                          className={css`
                            width: 100%;
                            height: 100%;
                            border-top: ${index === 0 ? 'none' : `1px solid ${theme.colors.neutral[300]}`};
                            align-items: center;
                            padding: 4px;
                            padding-left: 10px;
                            padding-right: 10px;
                            cursor: ${row.onClick ? 'pointer' : 'default'};
                          `}
                        >
                          <Typography style={{ fontSize: 12 }}>{row.title}:</Typography>
                          <FlexSpacer />
                          <Typography style={{ fontSize: 12, fontWeight: 700 }}>{row.value}</Typography>
                          {row.onClick && <EditOutlined />}
                        </FlexSpace>
                      </div>
                    ),
                  )}
                </FlexSpace>
              </DailyWorkflowCardBase>
              <FlexSpace
                className={css`
                  width: 100%;
                `}
                direction="row"
                gap={8}
                wrap
              >
                <DailyWorkflowCardBase title="Replenishment Path" headerActions={<></>}>
                  <div
                    className={css`
                      width: 100%;
                      min-height: 48px;
                    `}
                  >
                    <ReplenishmentPathsPanelContent
                      itemUid={dailyWorkflowData.itemId}
                      itemId={dailyWorkflowData.itemCode}
                      locationId={splitIdNameStr(dailyWorkflowData.location).foreignId}
                    />
                  </div>
                </DailyWorkflowCardBase>
                <DailyWorkflowCardBase title="Units of Measure" headerActions={<></>}>
                  <div
                    className={css`
                      width: 100%;
                      min-height: 48px;
                    `}
                  >
                    <UnitOfMeasuresPanelContent itemId={dailyWorkflowData.itemCode} />
                  </div>
                </DailyWorkflowCardBase>
              </FlexSpace>
              <FlexSpace
                className={css`
                  width: 100%;
                `}
                direction="row"
                gap={8}
              >
                <DailyWorkflowValueCard
                  title="Net Stock"
                  currentValue={
                    useAggregatedNetstock ? dailyWorkflowData.aggregatedNetStock : dailyWorkflowData.netStock
                  }
                  description="Net Stock"
                  subText={`${dailyWorkflowData.planningUnitOfMeasure} units`}
                  headerActions={
                    <NetStockPopover
                      isAggregatedNetStock={useAggregatedNetstock}
                      record={{
                        ...dailyWorkflowData,
                        netStock: useAggregatedNetstock
                          ? dailyWorkflowData.aggregatedNetStock
                          : dailyWorkflowData.netStock,
                        qtyOnHand: dailyWorkflowData.qtyOnHand,
                        unitOfMeasure: dailyWorkflowData.planningUnitOfMeasure,
                      }}
                      title="Net Stock"
                    />
                  }
                />
                <DailyWorkflowValueCard
                  title="Quantity Available"
                  currentValue={dailyWorkflowData.qtyAvailable}
                  subText={`${dailyWorkflowData.planningUnitOfMeasure} units`}
                />
              </FlexSpace>
            </FlexSpace>
          </FlexSpace>
        </div>
        <FlexSpace
          direction="column"
          gap={12}
          className={css`
            max-width: 300px;
            min-width: 300px;
            height: 100%;
            padding: 12px;
            margin-bottom: auto;
          `}
        >
          {/* AI Gut Check Card */}
          <div
            className={css`
              padding: 12px;
              background-color: ${theme.colors.primary[100]};
              border-radius: 6px;
              width: 100%;
            `}
          >
            <FlexSpace direction="column" gap={24}>
              <FlexSpace
                direction="row"
                gap={6}
                className={css`
                  width: 100%;
                `}
              >
                <div
                  className={css`
                    width: 24px;
                    height: 24px;
                    background-color: ${theme.colors.primary[500]};
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  `}
                >
                  {pageState?.gutChecks?.[itemLocationString]?.recommendedAction ? (
                    pageState.gutChecks[itemLocationString].recommendedAction === 'Accept' ? (
                      <LikeOutlined style={{ color: theme.colors.primary[100] }} />
                    ) : (
                      <DislikeOutlined style={{ color: theme.colors.primary[100] }} />
                    )
                  ) : null}
                </div>
                <Typography style={{ fontSize: 12, fontWeight: 700, color: theme.colors.primary[500] }}>
                  Cue: AI Summary + Advice
                </Typography>
                <FlexSpacer />
                <Tooltip title="Cue uses recent demand, forecasts, and rule triggers to spot patterns, providing a summary and suggested next steps based on trends and AI insights. Combine this with your judgment before acting. To adjust Cue’s guidance, contact your CSM.">
                  <QuestionCircleOutlined />
                </Tooltip>
              </FlexSpace>
              {isGutCheckLoading ? (
                <FlexSpace
                  direction="column"
                  gap={24}
                  alignItems="center"
                  className={css`
                    width: 100%;
                  `}
                >
                  <LoadingOutlined style={{ fontSize: 24 }} />
                  <Typography style={{ fontSize: 12 }}>Running AI Gut Check...</Typography>
                </FlexSpace>
              ) : pageState.gutChecks[itemLocationString] ? (
                <>
                  <FlexSpace direction="column" gap={4}>
                    <Typography style={{ fontSize: 12, fontWeight: 700 }}>Why this item needs review</Typography>
                    <Typography style={{ fontSize: 12 }}>
                      {pageState.gutChecks[itemLocationString].explanation}
                    </Typography>
                  </FlexSpace>
                  <FlexSpace direction="column" gap={4}>
                    <Typography style={{ fontSize: 12, fontWeight: 700 }}>Recommended Action</Typography>
                    <Typography style={{ fontSize: 12 }}>
                      {pageState.gutChecks[itemLocationString].recommendedAction}
                    </Typography>
                  </FlexSpace>
                </>
              ) : (
                <FlexSpace
                  direction="column"
                  gap={24}
                  alignItems="center"
                  className={css`
                    width: 100%;
                  `}
                >
                  <Typography style={{ fontSize: 12 }}>No AI Summary data available</Typography>
                </FlexSpace>
              )}
            </FlexSpace>
          </div>
          {searchPlanningItem && (
            <div
              className={css`
                display: flex;
                flex-direction: row;
                :hover {
                  cursor: pointer;
                }
              `}
              onClick={() => {
                showAsyncModal(AuditLogModal, {
                  record: searchPlanningItem,
                });
              }}
            >
              <AuditOutlined />
              <Typography type="subtitle" style={{ marginLeft: '4px', textDecoration: 'underline' }}>
                View Item Audit Log
              </Typography>
            </div>
          )}
          <Button
            type={previouslyAccepted ? 'primary' : 'default'}
            className={getActionButtonStyle('Accept')}
            disabled={!dailyWorkflowData || !validateMinAndMax()}
            onClick={handleAccept}
          >
            <CheckOutlined
              style={{
                color: getIconColor('Accept', Boolean(previouslyAccepted)),
              }}
            />
            <FlexSpacer />
            <Typography style={getButtonTextStyle('Accept', Boolean(previouslyAccepted))}>Accept</Typography>
            <FlexSpacer />
          </Button>
          <Tooltip
            title={`This will exclude until ${formatDate(excludedUntil)} (default duration from settings)`}
            placement="left"
          >
            <Button
              className={getActionButtonStyle('Override and Exclude')}
              type={previouslyOverridden ? 'primary' : 'default'}
              onClick={async () => {
                const resolutionData = {
                  min: dailyWorkflowData.currentMin,
                  max: dailyWorkflowData.currentMax,
                  currentMin: dailyWorkflowData.currentMin,
                  currentMax: dailyWorkflowData.currentMax,
                  recommendedMin: dailyWorkflowData.recommendedMin,
                  recommendedMax: dailyWorkflowData.recommendedMax,
                  projectedInventoryChange: calculatedProjectedInventoryChange,
                  excludeUntil: excludedUntil,
                };

                const updateResponse = await onOverrideAndExclude({
                  itemId: dailyWorkflowData.itemId,
                  locationId: splitIdNameStr(dailyWorkflowData.location).foreignId,
                  resolutionData,
                  resolution: DailyWorkflowTaskResolution.Excluded,
                });

                if (updateResponse.data.success) {
                  updatePageState(resolutionData);

                  track(TrackEvent.DailyWorkflow_OverrideAndExcludeDailyWorkflowItem, {
                    itemId: dailyWorkflowData.itemId,
                    locationId: splitIdNameStr(dailyWorkflowData.location).foreignId,
                    source: trackEventSource,
                    ...resolutionData,
                  });

                  handleMoveItem('next');
                }
              }}
            >
              <SettingOutlined
                style={{
                  color: getIconColor('Override and Exclude', Boolean(previouslyOverridden)),
                }}
              />
              <FlexSpacer />
              <Typography style={getButtonTextStyle('Override and Exclude', Boolean(previouslyOverridden))}>
                Exclude
              </Typography>
              <FlexSpacer />
            </Button>
          </Tooltip>

          {searchPlanningItem && (
            <Button
              type={prevResolutionData?.flagReason ? 'primary' : 'default'}
              className={getActionButtonStyle('Flag')}
              onClick={async () => {
                const response = await showAsyncModal(FlagItemLocationModal, {
                  selection: {
                    type: 'list',
                    items: [searchPlanningItem],
                    count: 1,
                  },
                  flagged: !dailyWorkflowData.flagged,
                });
                if (response) {
                  const resolutionData: UpdateDailyWorkflowTaskBodyParams['resolutionData'] = {
                    min,
                    max,
                    currentMin: dailyWorkflowData.currentMin,
                    currentMax: dailyWorkflowData.currentMax,
                    recommendedMin: dailyWorkflowData.recommendedMin,
                    recommendedMax: dailyWorkflowData.recommendedMax,
                    projectedInventoryChange: calculatedProjectedInventoryChange,
                    flagReason: response.comment,
                  };

                  const updateResponse = await onFlag({
                    itemId: dailyWorkflowData.itemId,
                    locationId: splitIdNameStr(dailyWorkflowData.location).foreignId,
                    resolutionData,
                    resolution: DailyWorkflowTaskResolution.Flagged,
                  });

                  if (updateResponse.data.success) {
                    updatePageState(resolutionData);

                    track(TrackEvent.DailyWorkflow_FlagDailyWorkflowItem, {
                      itemId: dailyWorkflowData.itemId,
                      locationId: splitIdNameStr(dailyWorkflowData.location).foreignId,
                      source: trackEventSource,
                      ...resolutionData,
                    });

                    handleMoveItem('next');
                  }
                }
              }}
            >
              <FlagOutlined
                style={{
                  color: getIconColor('Flag', Boolean(prevResolutionData?.flagReason)),
                }}
              />
              <FlexSpacer />
              <Typography style={getButtonTextStyle('Flag', Boolean(prevResolutionData?.flagReason))}>Flag</Typography>
              <FlexSpacer />
            </Button>
          )}
          <Tooltip title="Clears all actions on this item and resets replenishment values to their original recommendations.">
            <Button type="default" className={getActionButtonStyle('Reset')} onClick={handleReset}>
              <RedoOutlined
                style={{
                  color: 'inherit',
                }}
              />
              <FlexSpacer />
              <Typography style={DefaultButtonStyle}>Reset</Typography>
              <FlexSpacer />
            </Button>
          </Tooltip>
        </FlexSpace>
      </FlexSpace>
    </div>
  );
}

const ActionButtonStyle = css`
  display: flex;
  align-items: center;
  width: 100%;

  &:disabled {
    .ant-typography {
      color: ${theme.colors.neutral[300]} !important;
    }
  }
`;

const SelectedActionButtonStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: 'white',
};

const DefaultButtonStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 400,
};
